/**
 * DataSource: Troca de Plantão (Shift Handover) — GUPM-104
 * ---------------------------------------------------------
 * Hoje (POC): leitura/escrita direta no Supabase + Edge Function
 *            verify-shift-pin para validar PIN do saindo.
 * Futuro: o mecanismo de step-up do saindo pode trocar para reauth
 *         Azure AD se GUPM-103 decidir nessa direção — apenas a
 *         implementação de `verifyPinAndConfirm` muda; UI não.
 */

import { supabase } from '@/lib/supabase';
import type { ChecklistItem, ChecklistResponse } from '@/domain/shift-handover/types';
import type { TotpErrorCode } from '@/domain';
import type { DataSourceMeta } from './types';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface OperatorOption {
  id: string;
  name: string;
  email: string;
  profile: 'operador' | 'tecnico_responsavel' | 'admin';
  /**
   * Tem autenticador cadastrado (GUPM-273). A troca exige DOIS TOTPs, então
   * quem não tem não pode ser entrante — e sem isto a impossibilidade só
   * aparecia depois dos nove itens do checklist, como `no_totp_set`.
   * Booleano derivado na view; o segredo nunca sai do `service_role`.
   */
  hasTotp: boolean;
}

export interface ShiftHandover {
  id: string;
  /**
   * `null` = plantão GLOBAL, cobre as três barragens (GUPM-273) — o caso de toda
   * linha criada a partir do card. Linha anterior conserva a barragem que a UI
   * resolvia só para conseguir gravar; é resíduo, não escopo.
   */
  damId: string | null;
  damCode: string | null;
  /** NULL = abertura de plantão (sem passagem) — GUPM-272. */
  operatorOutId: string | null;
  operatorOutName: string | null;
  operatorInId: string;
  operatorInName: string;
  /**
   * LEGADO, congelado no GUPM-273: checklist da abertura/passagem, do modelo em
   * que a atestação era do COMEÇO do turno. `null` em toda linha criada a partir
   * do card — a atestação passou a ser do fim, em `endChecklistItems`.
   */
  checklistItems: ChecklistResponse[] | null;
  observation: string | null;
  startedAt: string;
  completedAt: string;
  /**
   * Encerramento sem rendição (GUPM-272 Rev.2). Antes estas colunas existiam no
   * banco mas não eram lidas por ninguém: encerrar um plantão não aparecia em
   * tela nenhuma, e o `/historico-trocas` rotulava "OK" um turno interrompido.
   */
  endedAt: string | null;
  endedById: string | null;
  endedByName: string | null;
  endReason: string | null;
  /**
   * Como o turno acabou (GUPM-273). `null` = ainda em curso, ou linha encerrada
   * antes do card (o backfill rotulou o histórico, então na prática só a atual).
   */
  endKind: 'rendicao' | 'solo' | 'admin' | null;
  /** Checklist do encerramento — NULL em destrave administrativo. */
  endChecklistItems: ChecklistResponse[] | null;
}

export interface ListHandoversFilter {
  damId?: string;
  from?: string;  // ISO date inclusive
  to?: string;    // ISO date inclusive (end of day)
  operatorId?: string; // saindo OU entrante
}

export interface CreateHandoverInput {
  operatorInId: string;
  responses: ChecklistResponse[];
  observation: string | null;
}

/**
 * Abertura de plantão (GUPM-272) — a operação estava sem plantonista e quem
 * registra é quem assume. Verbo separado de `createHandover` de propósito:
 * abrir plantão e passar plantão são atos diferentes, com autores diferentes
 * (`operator_in` vs `operator_out`) e regras diferentes no banco.
 */
export interface OpenShiftInput {
  /**
   * Obrigatória — justifica a exceção na auditoria (CHECK no banco).
   *
   * Não há checklist aqui (GUPM-273): quem acabou de chegar não viu nada para
   * atestar. A atestação é do FIM do turno.
   */
  observation: string;
}

export type TotpVerifyResult =
  | { success: true; engineerId: string }
  | { success: false; error: TotpErrorCode };

export interface TotpEnrollment {
  secret: string;
  otpauthUri: string;
  issuer: string;
  accountLabel: string;
}

export interface CurrentUserRow {
  id: string;
  name: string;
  email: string;
  profile: 'operador' | 'tecnico_responsavel' | 'admin';
  barragemIds: string[];
}

/** Holder do plantão ativo — retorno mínimo da RPC `get_current_shift` (GUPM-261). */
export interface CurrentShift {
  handoverId: string;
  operatorInId: string;
  operatorInName: string;
  since: string;
  /**
   * O plantonista ainda está ativo no cadastro (GUPM-273). `false` = ele foi
   * desligado e o plantão TRAVOU: o trigger de cadeia exige operador ativo em
   * quem sai, então nem ele passa o plantão nem outro assume — só destrave
   * administrativo resolve. Ele continua aparecendo de propósito; sumir faria a
   * operação parecer sem plantão quando na verdade está presa.
   */
  holderIsActive: boolean;
}

/** Para que o TOTP está sendo validado — mesma lista da verify-totp (GUPM-335). */
export type FinalidadeTotp =
  | 'plantao_abertura'
  | 'plantao_troca_saida'
  | 'plantao_troca_entrada'
  | 'plantao_encerramento'
  | 'baixa_tarp';

export interface ShiftHandoverDataSource extends DataSourceMeta {
  listChecklistItems(): Promise<ChecklistItem[]>;
  listActiveOperators(damCode: string): Promise<OperatorOption[]>;
  /** Resolve o registro do app_users_public para o usuário autenticado. */
  getCurrentUser(): Promise<CurrentUserRow | null>;
  /**
   * Plantão ativo (ou null). O plantão é GLOBAL — quem está de plantão cobre as
   * 3 barragens, então não recebe barragem. Via RPC security definer: visível a
   * QUALQUER autenticado, inclusive operador que nunca participou de troca (a
   * RLS da tabela o esconderia). GUPM-261.
   */
  getCurrentShift(): Promise<CurrentShift | null>;
  /**
   * Encerra o plantão ativo SEM iniciar outro. Dois usos:
   *   - o próprio plantonista sai sem rendição — `checklist` OBRIGATÓRIO, porque
   *     quem larga a vigilância atesta como a barragem ficou (GUPM-272 Rev.2);
   *   - o admin destrava plantão esquecido — sem checklist: ele não viveu o
   *     turno, e atestação falsa é pior que nenhuma (GUPM-261).
   * O banco recusa auto-encerramento sem checklist. Motivo sempre obrigatório;
   * TOTP validado ANTES, pelo hook — ver `useCloseShift`.
   */
  closeCurrentShift(reason: string, checklist?: ChecklistResponse[]): Promise<void>;
  /** Histórico paginado de handovers (engenheiro/admin enxerga tudo, operador só os próprios via RLS). */
  listHandovers(filter: ListHandoversFilter): Promise<ShiftHandover[]>;
  createHandover(input: CreateHandoverInput): Promise<ShiftHandover>;
  /** Abre plantão em nome próprio quando não há plantonista. GUPM-272. */
  openShift(input: OpenShiftInput): Promise<ShiftHandover>;
  /** Valida código TOTP de um engineer alvo (saindo=self ou entrante=outro). */
  /**
   * `finalidade` (GUPM-335) vai para a trilha: sem ela, a baixa de uma TARP e a
   * troca de plantão saíam iguais (`shift_totp_success`).
   */
  verifyTotp(
    targetEngineerId: string,
    code: string,
    finalidade?: FinalidadeTotp,
  ): Promise<TotpVerifyResult>;
  /**
   * Valida código TOTP contra um secret candidato (modo enrollment, /perfil/totp).
   * Server-side gatekeep — só persistir via confirmTotpEnrollment após sucesso.
   * Restrito a self-enrollment (targetEngineerId precisa bater com user autenticado).
   */
  verifyEnrollmentTotp(
    targetEngineerId: string,
    code: string,
    candidateSecret: string,
  ): Promise<TotpVerifyResult>;
  /** Gera um secret novo para o cadastro (não persiste — fluxo de enrollment). */
  provisionTotp(): Promise<TotpEnrollment>;
  /** Confirma o cadastro: persiste o secret após o usuário validar o primeiro código. */
  confirmTotpEnrollment(secret: string): Promise<void>;
}

function unwrapOne<T extends { name: string } | { code: string }>(
  raw: T | T[] | null,
): T | null {
  if (Array.isArray(raw)) return raw[0] ?? null;
  return raw;
}

/**
 * Shape do SELECT abaixo (NÃO da tabela inteira) — usado em listHandovers
 * e createHandover. Mantém os 8 campos base de shift_handovers + 3 joins
 * (dam.code, operator_out.name, operator_in.name) já projetados pelos
 * app_users_public/dams via FK.
 *
 * Se o SELECT mudar (adicionar/remover coluna ou join), atualize aqui —
 * o `.returns<HandoverRow>()` faz o TS validar o shape no ponto de uso
 * em vez de um `as` cast cego.
 */
interface HandoverRow {
  id: string;
  dam_id: string | null;
  operator_out_id: string | null;
  operator_in_id: string;
  /** LEGADO (GUPM-273): só linha anterior ao card tem. Ver `ShiftHandover`. */
  checklist_items: ChecklistResponse[] | null;
  observation: string | null;
  started_at: string;
  completed_at: string;
  ended_at: string | null;
  ended_by: string | null;
  end_reason: string | null;
  end_kind: 'rendicao' | 'solo' | 'admin' | null;
  end_checklist_items: ChecklistResponse[] | null;
  dam?: { code: string } | { code: string }[] | null;
  operator_out?: { name: string } | { name: string }[] | null;
  operator_in?: { name: string } | { name: string }[] | null;
  ended_by_user?: { name: string } | { name: string }[] | null;
}

function mapHandoverRow(row: HandoverRow): ShiftHandover {
  const dam = unwrapOne(row.dam ?? null);
  const opOut = unwrapOne(row.operator_out ?? null);
  const opIn = unwrapOne(row.operator_in ?? null);
  const endedBy = unwrapOne(row.ended_by_user ?? null);
  return {
    id: row.id,
    damId: row.dam_id,
    // `null` (plantão global) ≠ '' (join que não resolveu). A tela precisa da
    // diferença: uma é a regra nova, a outra é dado faltando.
    damCode: row.dam_id === null ? null : dam && 'code' in dam ? dam.code : '',
    operatorOutId: row.operator_out_id,
    // null (abertura) ≠ '' (nome não resolveu no join). A tela precisa da
    // diferença para rotular "sem passagem" em vez de mostrar célula vazia.
    operatorOutName:
      row.operator_out_id === null ? null : opOut && 'name' in opOut ? opOut.name : '',
    operatorInId: row.operator_in_id,
    operatorInName: opIn && 'name' in opIn ? opIn.name : '',
    checklistItems: row.checklist_items,
    observation: row.observation,
    startedAt: row.started_at,
    completedAt: row.completed_at,
    endedAt: row.ended_at,
    endedById: row.ended_by,
    endedByName:
      row.ended_by === null ? null : endedBy && 'name' in endedBy ? endedBy.name : '',
    endReason: row.end_reason,
    endKind: row.end_kind,
    endChecklistItems: row.end_checklist_items,
  };
}

/** Projeção compartilhada por listHandovers, createHandover e openShift. */
const HANDOVER_SELECT = `id, dam_id, operator_out_id, operator_in_id, checklist_items, observation, started_at, completed_at,
         ended_at, ended_by, end_reason, end_kind, end_checklist_items,
         dam:dams!shift_handovers_dam_id_fkey(code),
         operator_out:app_users_public!shift_handovers_operator_out_id_fkey(name),
         operator_in:app_users_public!shift_handovers_operator_in_id_fkey(name),
         ended_by_user:app_users_public!shift_handovers_ended_by_fkey(name)`;

/** app_users.id do usuário autenticado (a tabela usa id próprio, não auth.uid()). */
async function resolveMyAppUserId(): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('não autenticado');
  const filter = user.email
    ? `auth_user_id.eq.${user.id},email.eq.${user.email}`
    : `auth_user_id.eq.${user.id}`;
  const { data: meRow, error } = await supabase
    .from('app_users_public')
    .select('id')
    .or(filter)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!meRow) throw new Error('engineer não encontrado para o usuário autenticado');
  return meRow.id;
}

function normalizeObservation(observation: string | null): string | null {
  const trimmed = observation?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
}

async function insertHandover(row: {
  dam_id: null;
  operator_out_id: null;
  operator_in_id: string;
  observation: string | null;
}): Promise<ShiftHandover> {
  const { data, error } = await supabase
    .from('shift_handovers')
    .insert(row)
    .select(HANDOVER_SELECT)
    .single()
    .returns<HandoverRow>();
  if (error) throw error;
  if (!data) throw new Error('insert de shift_handover não retornou row');
  return mapHandoverRow(data);
}

function rowToChecklistItem(r: {
  id: string;
  order_index: number;
  label: string;
  related_module: string | null;
  is_active: boolean;
  group_order: number;
  group_label: string;
  group_color: string;
  safe_answer: string;
}): ChecklistItem {
  const color: ChecklistItem['group_color'] =
    r.group_color === 'red' || r.group_color === 'amber' || r.group_color === 'green'
      ? r.group_color
      : 'green';
  const safe: ChecklistItem['safe_answer'] = r.safe_answer === 'nao' ? 'nao' : 'sim';
  return {
    id: r.id,
    order_index: r.order_index,
    label: r.label,
    related_module: r.related_module,
    is_active: r.is_active,
    group_order: r.group_order,
    group_label: r.group_label,
    group_color: color,
    safe_answer: safe,
  };
}

export const supabaseShiftHandover: ShiftHandoverDataSource = {
  mode: 'manual',

  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const filter = user.email
      ? `auth_user_id.eq.${user.id},email.eq.${user.email}`
      : `auth_user_id.eq.${user.id}`;
    const { data, error } = await supabase
      .from('app_users_public')
      .select('id, name, email, profile, barragem_ids')
      .or(filter)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return {
      id: data.id,
      name: data.name,
      email: data.email ?? '',
      profile: data.profile as CurrentUserRow['profile'],
      barragemIds: data.barragem_ids ?? [],
    };
  },

  async listChecklistItems() {
    const { data, error } = await supabase
      .from('shift_checklist_items')
      .select(
        'id, order_index, label, related_module, is_active, group_order, group_label, group_color, safe_answer',
      )
      .eq('is_active', true)
      .order('order_index');
    if (error) throw error;
    return (data ?? []).map(rowToChecklistItem);
  },

  async listActiveOperators(damCode) {
    // Filtra explicitamente profile='operador' — apenas operadores assumem
    // turno. Engenheiros/admins consultam histórico, não atuam como
    // entrante. Atende ao Cenário 5 do requisitos.md.
    // Lê de app_users_public (view sem totp_secret_base32) — RLS hardening GUPM-111.
    const { data, error } = await supabase
      .from('app_users_public')
      .select('id, name, email, profile, barragem_ids, has_totp')
      .eq('profile', 'operador');
    if (error) throw error;
    return (data ?? [])
      .filter((r) => Array.isArray(r.barragem_ids) && r.barragem_ids.includes(damCode))
      .map((r) => ({
        id: r.id,
        name: r.name,
        email: r.email ?? '',
        profile: r.profile as OperatorOption['profile'],
        // `=== true` e não coerção: view antiga (release parcial) devolveria
        // `undefined`, e o falsy marcaria TODO mundo como sem autenticador,
        // travando a troca para a operação inteira. Aqui o padrão seguro é o
        // oposto do usual — na dúvida, deixar tentar: quem não tem esbarra na
        // Edge Function, que é onde a regra de verdade mora.
        hasTotp: r.has_totp !== false,
      }));
  },

  async getCurrentShift() {
    const { data, error } = await supabase.rpc('get_current_shift');
    if (error) throw error;
    const row = data?.[0];
    if (!row) return null;
    return {
      handoverId: row.handover_id,
      operatorInId: row.operator_in_id,
      operatorInName: row.operator_in_name,
      since: row.since,
      // Mesma razão do `hasTotp`: função antiga (release parcial) omite o campo,
      // e tratar ausência como `false` acusaria de inativo um plantonista
      // perfeitamente ativo, mandando a operação chamar um destrave que não é
      // necessário.
      holderIsActive: row.holder_is_active !== false,
    };
  },

  async closeCurrentShift(reason, checklist) {
    // `p_checklist` só vai quando existe: no destrave o banco ignora, mas mandar
    // array vazio pareceria "atestou e não respondeu nada".
    const { error } = await supabase.rpc('close_current_shift', {
      p_reason: reason,
      ...(checklist && checklist.length > 0 ? { p_checklist: checklist } : {}),
    });
    if (error) throw error;
  },

  async listHandovers(filter) {
    let q = supabase
      .from('shift_handovers')
      .select(HANDOVER_SELECT)
      .is('deleted_at', null)
      .order('completed_at', { ascending: false })
      .limit(200);
    if (filter.damId) q = q.eq('dam_id', filter.damId);
    if (filter.from) q = q.gte('completed_at', filter.from);
    if (filter.to) q = q.lte('completed_at', filter.to);
    if (filter.operatorId) {
      // Defensivo: o valor vem de dropdown de UUIDs + RLS protege, mas
      // .or() do PostgREST interpola direto na string de filtro — um valor
      // com sintaxe especial poderia manipular o filtro. Validar formato
      // antes elimina a superfície.
      if (!UUID_RE.test(filter.operatorId)) {
        throw new Error('operatorId deve ser UUID v4 válido');
      }
      q = q.or(`operator_out_id.eq.${filter.operatorId},operator_in_id.eq.${filter.operatorId}`);
    }
    const { data, error } = await q.returns<HandoverRow[]>();
    if (error) throw error;
    return (data ?? []).map(mapHandoverRow);
  },

  async createHandover(input) {
    // GUPM-273: passar o plantão deixou de ser um INSERT. Fechar o turno de quem
    // sai e abrir o de quem entra têm que ser uma transação só — entre as duas a
    // operação ficaria sem plantonista registrado. A policy de INSERT já não
    // aceita troca, então esta RPC é o único caminho.
    //
    // O checklist vai como atestação de SAÍDA (é de quem sai), e a observação
    // escrita vira o motivo do encerramento: numa troca ela só existe para
    // justificar divergência, que é exatamente o que o banco exige.
    const { error } = await supabase.rpc('close_current_shift', {
      p_reason: normalizeObservation(input.observation),
      p_checklist: input.responses,
      p_operator_in_id: input.operatorInId,
    });
    if (error) throw error;

    // A RPC devolve o id do turno FECHADO; a tela quer o que começou.
    //
    // Amarrado ao turno ABERTO e ao entrante, não só à linha mais recente. Sem
    // os dois filtros, uma regressão que fizesse a RPC não abrir a linha nova
    // devolveria a linha recém-FECHADA como se fosse o plantão que começou —
    // sem erro nenhum, que é o pior jeito de errar.
    const { data, error: erroLeitura } = await supabase
      .from('shift_handovers')
      .select(HANDOVER_SELECT)
      .is('deleted_at', null)
      .is('ended_at', null)
      .eq('operator_in_id', input.operatorInId)
      .order('completed_at', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1)
      .single()
      .returns<HandoverRow>();
    if (erroLeitura) throw erroLeitura;
    if (!data) throw new Error('troca gravada mas não foi possível reler o plantão');
    return mapHandoverRow(data);
  },

  async openShift(input) {
    // Quem abre é quem assume: `operator_in_id` é o próprio usuário e não há
    // saindo. A policy de INSERT e o trigger de cadeia confirmam os dois no
    // banco — este método não é o gate, é a porta certa.
    const meId = await resolveMyAppUserId();
    return insertHandover({
      // GUPM-273: NULL = global. O plantonista cobre as três barragens; exigir
      // uma estrutura aqui era resíduo do `NOT NULL`, e era o que produzia
      // "Barragem indisponível — recarregue a página" no rodapé do formulário.
      dam_id: null,
      operator_out_id: null,
      operator_in_id: meId,
      observation: normalizeObservation(input.observation),
    });
  },

  async verifyTotp(targetEngineerId, code, finalidade) {
    const { data, error } = await supabase.functions.invoke<TotpVerifyResult>('verify-totp', {
      body: { targetEngineerId, code, finalidade },
    });
    if (error) {
      const status = (error as { context?: { status?: number } }).context?.status;
      if (status === 401) return { success: false, error: 'unauthorized' };
      if (status === 423) return { success: false, error: 'locked_out' };
      if (status === 412) return { success: false, error: 'no_totp_set' };
      if (status === 404) return { success: false, error: 'not_found' };
      // Edge fora do ar (503) — distinguir de código errado para não mandar o RT
      // ficar retentando um TOTP que está certo (GUPM-184 self-review).
      if (status === 503) return { success: false, error: 'service_unavailable' };
      // Sem status HTTP = a requisição não chegou (rede/CORS/DNS). Não é código
      // errado (GUPM-234): reportar `invalid_code` faz o RT retentar um código
      // correto e caminhar para o lockout de 15 min por um problema de rede.
      if (status === undefined) return { success: false, error: 'service_unavailable' };
      return { success: false, error: 'invalid_code' };
    }
    return data ?? { success: false, error: 'invalid_code' };
  },

  async verifyEnrollmentTotp(targetEngineerId, code, candidateSecret) {
    const { data, error } = await supabase.functions.invoke<TotpVerifyResult>('verify-totp', {
      body: { targetEngineerId, code, candidateSecret },
    });
    if (error) {
      const status = (error as { context?: { status?: number } }).context?.status;
      if (status === 401) return { success: false, error: 'unauthorized' };
      if (status === 403) return { success: false, error: 'enrollment_self_only' };
      if (status === 404) return { success: false, error: 'not_found' };
      if (status === 503) return { success: false, error: 'service_unavailable' };
      // Mesma regra do `verifyTotp` (GUPM-234): sem status, a requisição não
      // chegou — o código digitado não tem culpa.
      if (status === undefined) return { success: false, error: 'service_unavailable' };
      return { success: false, error: 'invalid_code' };
    }
    return data ?? { success: false, error: 'invalid_code' };
  },

  async provisionTotp() {
    const { data, error } = await supabase.functions.invoke<TotpEnrollment>('provision-totp', {
      body: {},
    });
    if (error) throw error;
    if (!data) throw new Error('provision-totp não retornou dados');
    return data;
  },

  async confirmTotpEnrollment(secret) {
    const { error } = await supabase.rpc('set_my_totp', { p_secret_base32: secret });
    if (error) throw error;
  },
};
